<template>
  <div class="card border-0 bg-light p-3 mb-3">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h6 class="mb-0">Dispositivos de terceiros liberados</h6>
      <small class="text-muted">Visualização no mapa (aba Público)</small>
    </div>
    <p class="text-muted small mb-3">
      Selecione as estações públicas (Defesa Civil, ANA, TideSat) que este usuário poderá ver
      no mapa. Administradores veem todas independentemente desta lista. Estes dispositivos
      não são expostos na API nem nos relatórios. Você pode definir um nome fictício por
      estação (opcional).
    </p>

    <div v-if="!userId" class="alert alert-warning py-2 mb-0">
      Salve o usuário primeiro para gerenciar os dispositivos de terceiros.
    </div>

    <template v-else>
      <div class="mb-3">
        <label class="form-label">Adicionar dispositivos</label>
        <!-- close-on-select=false: permite marcar vários dispositivos numa única abertura
             do dropdown. clear-on-select/preserve-search mantêm o texto digitado, para
             adicionar várias estações da mesma busca sem redigitar. -->
        <VueMultiselect
          v-model="selected"
          :options="options"
          :multiple="true"
          :show-labels="false"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :hide-selected="true"
          :loading="loadingOptions"
          placeholder="Buscar por nome, código, cidade ou rio"
          label="display"
          track-by="externalDeviceId"
          @search-change="onSearchChange"
          :internal-search="false"
        >
          <template v-slot:noResult>Nenhum dispositivo encontrado.</template>
          <template v-slot:noOptions>Digite para buscar.</template>
        </VueMultiselect>
      </div>

      <div v-if="selected.length > 0" class="mb-0">
        <label class="form-label">Nome fictício (opcional)</label>
        <div
          v-for="item in selected"
          :key="item.externalDeviceId"
          class="d-flex align-items-center mb-2 gap-2 user-api-device-row"
        >
          <div class="device-tag">
            <span class="device-tag-name">{{ item.nome || "(sem nome)" }}</span>
            <span class="device-tag-code">{{ item.fonteLabel }} · {{ item.localizacao }}</span>
          </div>
          <input
            type="text"
            class="form-control device-tag-input"
            v-model="item.alias"
            maxlength="120"
            placeholder="Nome fictício (deixe vazio para usar o nome original)"
          />
          <button
            type="button"
            class="btn btn-outline-danger device-tag-remove"
            @click="removeRow(item)"
            title="Remover"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>
      </div>

      <div v-if="rejected.length > 0" class="alert alert-warning mt-3 mb-0" role="alert">
        <div class="d-flex justify-content-between align-items-start mb-1">
          <strong>
            <i class="bx bx-error-circle me-1"></i>
            {{ rejected.length }}
            {{ rejected.length === 1 ? 'dispositivo não foi salvo' : 'dispositivos não foram salvos' }}
          </strong>
          <button type="button" class="btn-close btn-sm" @click="rejected = []" aria-label="Fechar"></button>
        </div>
        <ul class="mb-0 ps-3 small">
          <li v-for="item in rejected" :key="'rej-' + item.externalDeviceId">
            <span class="text-muted">Dispositivo #{{ item.externalDeviceId }}:</span> {{ item.reason }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import VueMultiselect from "vue-multiselect";

// Espelha UserApiDevices.vue, mas para estações de terceiros: aqui o acesso é de
// VISUALIZAÇÃO (mapa), não de leitura via API.
export default {
  name: "UserExternalDevices",
  components: { VueMultiselect },
  props: {
    userId: { type: [Number, String], default: null },
  },
  data() {
    return {
      selected: [],
      options: [],
      loadingOptions: false,
      searchDebounce: null,
      rejected: [],
    };
  },
  watch: {
    userId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadAccesses();
          this.loadOptions("");
        } else {
          this.selected = [];
          this.options = [];
          this.rejected = [];
        }
      },
    },
  },
  methods: {
    decorate(item) {
      const localizacao = [item.cidade, item.uf].filter(Boolean).join("/");
      return {
        externalDeviceId: item.externalDeviceId,
        externalId: item.externalId,
        fonte: item.fonte,
        fonteLabel: item.fonteLabel,
        nome: item.nome,
        cidade: item.cidade,
        uf: item.uf,
        rio: item.rio,
        localizacao: localizacao || "—",
        alias: item.alias || "",
        // Fonte no rótulo porque estações homônimas em órgãos diferentes são comuns.
        display:
          (item.nome || "(sem nome)") +
          " — " + (item.fonteLabel || item.fonte) +
          (localizacao ? " (" + localizacao + ")" : ""),
      };
    },
    loadAccesses() {
      this.urlPost("/AdminUserExternalDeviceAccess/Read", { userId: Number(this.userId) })
        .then((res) => {
          this.selected = (res.data.itens || []).map((i) => this.decorate(i));
        })
        .catch((e) => {
          console.error(e);
        });
    },
    loadOptions(search) {
      this.loadingOptions = true;
      this.urlPost("/AdminUserExternalDeviceAccess/Available", {
        search: search || "",
        limit: 100,
      })
        .then((res) => {
          // Sem filtrar os já selecionados aqui: com o dropdown permanecendo aberto,
          // quem esconde os escolhidos é o hide-selected do componente, em tempo real.
          // Filtrar no fetch faria a lista mudar sozinha entre buscas.
          this.options = (res.data.itens || []).map((i) => this.decorate(i));
        })
        .finally(() => {
          this.loadingOptions = false;
        });
    },
    onSearchChange(query) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.loadOptions(query);
      }, 300);
    },
    removeRow(item) {
      this.selected = this.selected.filter(
        (s) => s.externalDeviceId !== item.externalDeviceId
      );
    },
    save() {
      if (!this.userId) return Promise.resolve();
      this.rejected = [];
      const payload = {
        userId: Number(this.userId),
        accesses: this.selected.map((s) => ({
          externalDeviceId: s.externalDeviceId,
          alias: s.alias && s.alias.trim() ? s.alias.trim() : null,
        })),
      };
      return this.urlPut("/AdminUserExternalDeviceAccess", payload).then((res) => {
        const data = res && res.data ? res.data : {};
        const rejected = Array.isArray(data.rejected) ? data.rejected : [];
        if (rejected.length > 0) {
          this.rejected = rejected;
          const rejectedIds = new Set(rejected.map((r) => r.externalDeviceId));
          this.selected = this.selected.filter(
            (s) => !rejectedIds.has(s.externalDeviceId)
          );
        }
        return res;
      });
    },
  },
};
</script>

<style scoped>
/* Mesmas classes de UserApiDevices.vue para os dois blocos ficarem idênticos. */
.user-api-device-row {
  flex-wrap: nowrap;
  align-items: stretch;
}
.user-api-device-row > * {
  height: 48px;
}
.device-tag {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  flex: 0 0 240px;
  min-width: 0;
  padding: 4px 12px;
  background-color: #fff;
  border: 1px solid #d9dee3;
  border-radius: 6px;
  line-height: 1.15;
  overflow: hidden;
}
.device-tag-name,
.device-tag-code {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.device-tag-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: #566a7f;
}
.device-tag-code {
  font-size: 0.7rem;
  color: #a1acb8;
  margin-top: 2px;
  letter-spacing: 0.3px;
}
.device-tag-input {
  flex: 1 1 auto;
  min-width: 0;
}
.device-tag-remove {
  flex: 0 0 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.device-tag-remove i {
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .user-api-device-row {
    flex-wrap: wrap;
  }
  .device-tag {
    flex: 1 1 calc(100% - 56px);
    order: 1;
  }
  .device-tag-remove {
    order: 2;
  }
  .device-tag-input {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
